<template>
  <div class="my-account-sidebar">
    <div id="default-preference-section"
         class="sidebar__container">
      <div class="sidebar__settings-header">
        <div class="sidebar__header sidebar__settings-title">
          {{ $t('MYACCOUNT.DEFAULT_EXPERIENCE') }}
        </div>
        <div class="sidebar__settings-text">
          <div class="sidebar__settings-description">
            {{ $t('MYACCOUNT.DEFAULT_EXPERIENCE_DESC') }}
          </div>
        </div>
      </div>
      <div id="default-homepage-error-notification">
        <utd-notification v-if="defaultPreferenceSaveError"
                          class="ds1-mb-2"
                          notification-mode="error">
          <template #title>Error</template>
          <span>{{ $t('MYACCOUNT.DEFAULT_EXPERIENCE_SAVE_ERROR') }}</span>
        </utd-notification>
      </div>
      <div class="sidebar__content sidebar__settings-content">
        <utd-radio-set class="sidebar__settings-options"
                       role="radiogroup"
                       :aria-label="$t('MYACCOUNT.DEFAULT_EXPERIENCE')"
                       :items="defaultExperienceOptions"
                       name="default-experience"
                       :value="selectedDefaultPreferenceAppIndex"
                       @input="updateDefaultPreferenceSelectionByIndex">
          <template #default="{ item: option }">
            <span class="sidebar__settings-option-content">
              <span class="sidebar__settings-option-icon"
                    :class="option.iconClass"
                    aria-hidden="true">
                <img v-if="option.icon"
                     class="sidebar__settings-option-icon-image"
                     :class="`sidebar__settings-option-icon-image--${option.value.toLowerCase()}`"
                     :src="option.icon"
                     alt="">
              </span>
              <span class="sidebar__settings-option-label">
                {{ option.label }}
              </span>
            </span>
          </template>
        </utd-radio-set>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, getCurrentInstance, ref, watch } from 'vue';
import { useStore } from 'vuex';
import UtdRadioSet from '_acaSrc/components/shared/stdlib/UtdRadioSet.vue';
import UtdNotification from '_acaSrc/components/shared/utd/UtdNotification.vue';
import { logUiClickEventHelper } from '_acaSrc/utility/Events';
import expertAiLogo from '~_acaAssets/utd-ai/utd-ai-spark.svg';
import utdWheelIcon from '~_acaAssets/logos/utd-wheel-icon.svg';

const DEFAULT_PREFERENCE_APP_OPTIONS = [ 'XAI', 'UTD', 'NONE' ];

const store = useStore();
const { appContext } = getCurrentInstance();

const selectedDefaultPreferenceApp = ref('NONE');
const defaultPreferenceSaveError = ref(false);
const translate = appContext.config.globalProperties.$t ?? (translationKey => translationKey);

const getDefaultExperienceLabel = value => {
    if (value === 'XAI') {
        return translate('MYACCOUNT.DEFAULT_EXPERIENCE_EXPERT_AI');
    }

    if (value === 'UTD') {
        return translate('MYACCOUNT.DEFAULT_EXPERIENCE_UPTODATE');
    }

    return translate('MYACCOUNT.DEFAULT_EXPERIENCE_NONE');
};

const myAccountDefaultPreferenceApp = computed(
    () => store.getters['account/myAccountDefaultPreferenceApp']
);

const defaultExperienceOptions = computed(() => DEFAULT_PREFERENCE_APP_OPTIONS.map(value => {
    const label = getDefaultExperienceLabel(value);

    return {
        value,
        ariaLabel: label,
        id: `default-experience-${value}`,
        icon: value === 'XAI' ? expertAiLogo : value === 'UTD' ? utdWheelIcon : null,
        iconClass: value === 'NONE' ? 'wkce-icon-stop-sign' : null,
        inputClass: 'sidebar__settings-option-radio',
        label,
        labelClass: 'sidebar__settings-option',
        option: label
    };
}));

const selectedDefaultPreferenceAppIndex = computed(() =>
    defaultExperienceOptions.value.findIndex(
        option => option.value === selectedDefaultPreferenceApp.value
    )
);

const normalizedDefaultPreferenceApp = computed(() => {
    return DEFAULT_PREFERENCE_APP_OPTIONS.includes(myAccountDefaultPreferenceApp.value)
        ? myAccountDefaultPreferenceApp.value
        : 'NONE';
});

watch(normalizedDefaultPreferenceApp, value => {
    selectedDefaultPreferenceApp.value = value;
}, { immediate: true });

const updateDefaultPreferenceSelection = async selectedPreferenceApp => {
    if (!selectedPreferenceApp
    || selectedPreferenceApp === normalizedDefaultPreferenceApp.value) {
        selectedDefaultPreferenceApp.value = normalizedDefaultPreferenceApp.value;
        defaultPreferenceSaveError.value = false;
        return;
    }

    selectedDefaultPreferenceApp.value = selectedPreferenceApp;
    defaultPreferenceSaveError.value = false;
    logUiClickEventHelper({
        uiElementName: 'defaultPreference_defaultAppSelection',
        optData: selectedPreferenceApp
    });
    const isSuccess = await store.dispatch(
        'account/updateDefaultPreferenceApp',
        selectedPreferenceApp
    );

    if (!isSuccess) {
        selectedDefaultPreferenceApp.value = normalizedDefaultPreferenceApp.value;
        defaultPreferenceSaveError.value = true;
    }
};

const updateDefaultPreferenceSelectionByIndex = selectedOptionIndex => {
    updateDefaultPreferenceSelection(defaultExperienceOptions.value[selectedOptionIndex]?.value);
};
</script>

<style lang="less" scoped>
@import (reference) '~_acaAssets/wkce/colors/wkce-app-styles';

.sidebar__settings-content {
  .ds1-mb-3();
  .ds1-ml-0();
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.sidebar__settings-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.sidebar__settings-header {
  .ds1-mb-1();

  .sidebar__header {
    .ds1-pb-compact();
  }
}

.sidebar__settings-title {
  padding-bottom: 4px;
}

.sidebar__settings-description {
  color: @WKCE-GRAY-SHADE2;
  font-size: 10px;
  line-height: 16px;
}

.sidebar__settings-options {
  display: flex;
  flex-direction: column;
  gap: 0;

  :deep(.wk-field-choice-label) {
    .ds1-pa-0();
    align-items: center;
    cursor: pointer;
    display: flex;
    flex-direction: row-reverse;
    justify-content: space-between;
    min-height: 40px;
    overflow: hidden;
    width: 100%;
  }

  :deep(.wk-field-choice) {
    appearance: none;
    background: @WKCE-WHITE;
    border: @DS1-UTD-GRAY-SINGLE-BORDER;
    border-radius: 50%;
    cursor: pointer;
    flex: 0 0 auto;
    height: 16px;
    margin: 0;
    position: relative;
    width: 16px;
  }

  :deep(.wk-field-choice:checked) {
    background: @WKCE-BLUE-SHADE1;
    border-color: @WKCE-BLUE-SHADE1;
  }

  :deep(.wk-field-choice:checked::after) {
    background: @WKCE-WHITE;
    border-radius: 50%;
    content: '';
    height: 4px;
    left: 50%;
    position: absolute;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 4px;
  }

  :deep(.wk-field-choice:focus-visible) {
    outline: @DS1-UTD-FOCUS-OUTLINE;
  }

  :deep(.wk-field-choice-text) {
    display: flex;
    flex: 1 1 auto;
    min-width: 0;
  }
}

.sidebar__settings-option-content {
  .ds1-pv-1();
  align-items: center;
  display: flex;
  gap: 8px;
  flex: 1 1 auto;
  min-width: 0;
}

.sidebar__settings-option-label {
  color: @WKCE-GRAY-SHADE1;
  font-size: 14px;
  line-height: 15.8px;
}

.sidebar__settings-option-icon {
  align-items: center;
  display: inline-flex;
  flex: 0 0 auto;
  height: 16px;
  justify-content: center;
  width: 16px;
}

.wkce-icon-stop-sign.sidebar__settings-option-icon {
  .ds1-ma-0();
  font-size: 16px;
}

.sidebar__settings-option-icon-image {
  display: block;
  height: auto;
  max-height: 16px;
  width: auto;
}

.sidebar__settings-option-icon-image--xai {
  max-width: 16px;
}

.sidebar__settings-option-icon-image--utd {
  width: 16px;
}
</style>
